// BHP-1063: DNS read-models and the client-side half of the record rules.
//
// Every rule here mirrors one the backend enforces (CreateDnsRecordDto +
// DnsRecordProcessor). The client copy exists to give feedback before the round
// trip — the server stays the authority, and anything it rejects is surfaced
// through the RFC 7807 body.

export const DNS_RECORD_TYPES = [
  'A',
  'AAAA',
  'CNAME',
  'MX',
  'TXT',
  'NS',
  'SRV',
  'CAA',
  'PTR',
  'SOA'
] as const;

export type DnsRecordType = (typeof DNS_RECORD_TYPES)[number];

/**
 * Cloudflare owns the nameserver records for a zone (set up by the backend on
 * registration), and the API answers 422 on any create, update or delete of an
 * NS record. Show them, never offer to edit them.
 */
export function isReadOnlyType(type: string): boolean {
  return type === 'NS';
}

/** MX and SRV are the types Cloudflare accepts — and requires — a priority for. */
export function requiresPriority(type: string): boolean {
  return type === 'MX' || type === 'SRV';
}

/** Outcome of the backend's Cloudflare sync, per record. */
export type DnsSyncStatus = 'pending' | 'synced' | 'failed';

export interface DnsRecord {
  uuid: string;
  type: DnsRecordType;
  name: string;
  value: string;
  priority: string;
  dnsZoneUuid: string | null;
  ttl: number;
  syncStatus: DnsSyncStatus;
  active: number | null;
  createdOn: string;
  updatedOn: string;
}

export interface DnsZone {
  uuid: string;
  dnsSec: string;
  domainUuid: string | null;
  dnsRecordUuids: string[];
  active: number | null;
}

export const TTL_MIN = 60;
export const TTL_MAX = 86400;
export const TTL_DEFAULT = 3600;

export interface DnsRecordInput {
  type: string;
  name: string;
  value: string;
  priority: string;
  ttl: number;
}

/** Field-keyed messages, ready to render next to the inputs. Empty = valid. */
export type DnsRecordErrors = Partial<Record<keyof DnsRecordInput, string>>;

/**
 * BHP-1048: the inputs the record form renders. Names match the property paths
 * in `CreateDnsRecordDto`, so a 422 violation binds straight to its input.
 * `dnsZoneUuid` is deliberately absent — the panel supplies it, the user never
 * types it, so a violation on it belongs in the form-level banner.
 */
export const DNS_RECORD_FIELDS: readonly string[] = [
  'type',
  'name',
  'value',
  'priority',
  'ttl'
];

const IPV4 =
  /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;
// Deliberately loose: hex groups and colons, with an optional `::` shortening.
// Full IPv6 grammar is not worth reimplementing — the server has the last word.
const IPV6 = /^[0-9a-f:]+$/i;

export function validateRecord(input: DnsRecordInput): DnsRecordErrors {
  const errors: DnsRecordErrors = {};

  if (input.type === '') {
    errors.type = 'Pick a record type.';
  } else if (!DNS_RECORD_TYPES.includes(input.type as DnsRecordType)) {
    errors.type = 'Unknown record type.';
  } else if (isReadOnlyType(input.type)) {
    errors.type = 'NS records are managed for you and cannot be edited.';
  }

  if (input.name.trim() === '') {
    errors.name = 'Name is required.';
  } else if (input.name.length > 255) {
    errors.name = 'Name must not exceed 255 characters.';
  }

  if (input.value.trim() === '') {
    errors.value = 'Value is required.';
  } else if (input.type === 'A' && !IPV4.test(input.value.trim())) {
    errors.value = 'An A record needs a valid IPv4 address.';
  } else if (
    input.type === 'AAAA' &&
    (!IPV6.test(input.value.trim()) || !input.value.includes(':'))
  ) {
    errors.value = 'An AAAA record needs a valid IPv6 address.';
  }

  if (requiresPriority(input.type) && !/^\d+$/.test(input.priority.trim())) {
    errors.priority = `${input.type} records need a numeric priority.`;
  }

  if (
    !Number.isInteger(input.ttl) ||
    input.ttl < TTL_MIN ||
    input.ttl > TTL_MAX
  ) {
    errors.ttl = `TTL must be between ${TTL_MIN} and ${TTL_MAX} seconds.`;
  }

  return errors;
}

export function hasErrors(errors: DnsRecordErrors): boolean {
  return Object.keys(errors).length > 0;
}
